import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  inject,
  Input,
  signal,
  ViewChild,
  ViewEncapsulation,
  WritableSignal,
} from '@angular/core';
import { TranslocoModule } from '@ngneat/transloco';
import SdDialog from '@solid-design-system/components/dist-versioned/components/dialog/dialog';
import { firstValueFrom } from 'rxjs';
import { LoadingIndicatorDirective } from 'src/app/common/directives/loading-indicator.directive';
import { SdDialogDestroyAfterCloseDirective } from 'src/app/common/directives/sd-dialog-destroy-component-after-close.directive';
import { SdSignalModelDirective } from 'src/app/common/directives/sd-signal-model.directive';
import { ApiSignal, toApiSignal } from 'src/app/common/util/api-signal';
import { Events } from 'src/app/util/events';
import { ShadowStylesDirective } from '../../../../boilerplate/shadow-styles.directive';
import { ColleaguesService, UserDeleteReason, UserInfo, UserService } from '../../../../openapi-client';
import { Dialog } from '../../../common/util/dialog';

@Component({
  selector: 'wct-user-delete-dialog',
  standalone: true,
  imports: [TranslocoModule, SdDialogDestroyAfterCloseDirective, LoadingIndicatorDirective, SdSignalModelDirective],
  templateUrl: './user-delete-dialog.component.html',
  styleUrl: './user-delete-dialog.component.scss',
  encapsulation: ViewEncapsulation.ShadowDom,
  hostDirectives: [ShadowStylesDirective],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class UserDeleteDialogComponent extends Dialog {
  private readonly userService = inject(UserService);
  private readonly colleaguesService = inject(ColleaguesService);

  @Input({ required: true }) public userInfo!: UserInfo;

  @Input({ required: false }) public message: string | undefined;
  @Input({ required: false }) public delete: string | undefined;
  @Input({ required: false }) public cancel: string | undefined;

  @ViewChild('dialog') protected dialog!: ElementRef<SdDialog>;

  protected readonly deleteReasons: ApiSignal<UserDeleteReason[]> = toApiSignal(
    this.colleaguesService.getColleagueDeletionReasons()
  );
  protected readonly loading: WritableSignal<boolean> = signal(false);

  protected readonly selectedReason: WritableSignal<string> = signal('');
  protected readonly comment: WritableSignal<string> = signal('');

  protected readonly selectKeyOther: string = '__other__';


  protected async submit(event: SubmitEvent, valid: boolean): Promise<void> {
    event.preventDefault();
    event.stopPropagation();

    if (this.loading() || !valid) {
      return;
    }

    const reason: string | undefined =
      this.selectedReason() === this.selectKeyOther
        ? this.comment()
        : this.deleteReasons()?.find(reason => reason.key === this.selectedReason())?.label;

    if (!reason) {
      return;
    }

    this.loading.set(true);

    try {
      await firstValueFrom(this.userService.deleteUser(this.userInfo.clientId, reason));
      Events.colleaguesModified.next();
      await this.hide();
    } finally {
      this.loading.set(false);
    }
  }

  protected async hide(): Promise<void> {
    await this.dialog.nativeElement.hide();
  }
}
